/**
 * A scene's walkable ground (lifted from the lantern-water prototype's
 * `main.js` `canWalk` and `groundHeight`, and the occupancy grid of the shared
 * engine's `navigation.js`), in game axes: x east, y north, `h` the height of
 * the ground, metres.
 *
 * - `canWalk(x, y)`: inside `bounds` by at least `walk.pad`; not within
 *   `walk.pad` of any collider; not inside a channel (the canal) unless inside
 *   one of its openings (a bridge, the boat landing). The prototype's three
 *   tests, in the same order; continuous, so a body slides along an edge.
 * - `heightAt(x, y)`: the highest surface under the point (a bridge's arch
 *   sampled along x), unless one of them is `exclusive` (the landing's steps
 *   below the quay), which wins at once; 0 where none is. `surfaceAt` names
 *   that surface (null where none is).
 * - `canWalk` does not test the surfaces, as the prototype's did not: off
 *   every surface the ground is at height 0. A scene with `walk.surfacesOnly`
 *   (the airship, ADR 0033: `airship-layout.js` `canWalk`) also needs ground
 *   under the body: its centre and 16 points on a circle `pad` + 0.01 m round
 *   it, each on a surface.
 * - `extras` (a ship scene's rooms, `@ab/scene` `shipWalkExtras`) adds
 *   surfaces and colliders the record does not list itself.
 * - `setDynamic` replaces the colliders that come and go (the airship's crew
 *   posts, ADR 0033: one stands only while someone works there) and walks
 *   the lattice again.
 * - The occupancy grid: `canWalk` at every node of a `walk.cell` lattice over
 *   `bounds` (0.4 m in the prototype), computed once; the route search
 *   (`path.ts`) reads it instead of testing colliders node by node.
 */
import type { Scene } from '@ab/content-schema';

export type Point = { readonly x: number; readonly y: number };
type Surface = Scene['surfaces'][number];

export type WalkMap = {
  readonly scene: Scene;
  /** The lattice: node (i, j) is the point (i × cell, j × cell); `walkable[(j - j0) × columns + (i - i0)]`. */
  readonly cell: number;
  readonly i0: number;
  readonly j0: number;
  readonly columns: number;
  readonly rows: number;
  readonly walkable: Uint8Array;
  readonly canWalk: (x: number, y: number) => boolean;
  readonly heightAt: (x: number, y: number) => number;
  /** The id of the surface `heightAt` reads at a point (the highest, or the exclusive one), or null on no surface. */
  readonly surfaceAt: (x: number, y: number) => string | null;
  /** Whether the lattice node (i, j) is walkable (false outside the lattice). */
  readonly node: (i: number, j: number) => boolean;
  /** Replaces the colliders that come and go (the crew's posts) and recomputes the lattice. */
  readonly setDynamic: (colliders: readonly Scene['colliders'][number][]) => void;
  /** The colliders that stand now besides the scene's own (`setDynamic`'s). */
  readonly dynamic: () => readonly Scene['colliders'][number][];
};

const inside = (s: Surface, x: number, y: number): boolean => {
  if (s.circle !== undefined) return Math.hypot(x - s.circle.x, y - s.circle.y) < s.circle.r;
  if (s.polygon !== undefined) {
    // Even-odd ray cast.
    let hit = false;
    const p = s.polygon;
    for (let i = 0, j = p.length - 1; i < p.length; j = i, i += 1) {
      const a = p[i];
      const b = p[j];
      if (a === undefined || b === undefined) continue;
      if (a[1] > y !== b[1] > y && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) hit = !hit;
    }
    return hit;
  }
  return x > (s.x0 ?? 0) && x < (s.x1 ?? 0) && y > (s.y0 ?? 0) && y < (s.y1 ?? 0);
};

/** A bridge's walking height along x: from `left` to `right`, plus `lift` and `rise × sin(πt)` (`world.js` `bridge`). */
const archAt = (a: NonNullable<Surface['arch']>, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a.from) / (a.to - a.from)));
  return a.left + (a.right - a.left) * t + a.lift + Math.sin(t * Math.PI) * a.rise;
};

/** A flight of steps down along x: `drop` lower every `width` past `from`, at most `count - 1` steps. */
const stepsAt = (st: NonNullable<Surface['steps']>, x: number): number => -Math.min(st.count - 1, Math.max(0, Math.floor((x - st.from) / st.width))) * st.drop;

export const surfaceHeight = (s: Surface, x: number): number => s.h + (s.arch === undefined ? 0 : archAt(s.arch, x)) + (s.steps === undefined ? 0 : stepsAt(s.steps, x));

/** The body's ring for `walk.surfacesOnly` (`airship-layout.js` `BODY`): 16 points on a circle `pad` + 0.01 m round the centre. */
const ring = (pad: number): readonly Point[] => Array.from({ length: 16 }, (_, i) => ({ x: Math.cos((i * Math.PI) / 8) * (pad + 0.01), y: Math.sin((i * Math.PI) / 8) * (pad + 0.01) }));

export const createWalkMap = (scene: Scene, extras?: { readonly surfaces: Scene['surfaces']; readonly colliders: Scene['colliders'] }): WalkMap => {
  const { pad, cell } = scene.walk;
  const b = scene.bounds;
  const surfaces = extras === undefined ? scene.surfaces : [...scene.surfaces, ...extras.surfaces];
  const colliders = extras === undefined ? scene.colliders : [...scene.colliders, ...extras.colliders];
  const body = scene.walk.surfacesOnly === true ? ring(pad) : null;
  let dynamic: readonly Scene['colliders'][number][] = [];
  // Edges count as ground (the prototype's `contains` is inclusive), so the seam between two touching surfaces is walkable.
  const onGround = (x: number, y: number): boolean =>
    (buckets.get(keyOf(Math.floor(x / bucketSize), Math.floor(y / bucketSize))) ?? []).some((s) =>
      s.circle === undefined && s.polygon === undefined ? x >= (s.x0 ?? 0) && x <= (s.x1 ?? 0) && y >= (s.y0 ?? 0) && y <= (s.y1 ?? 0) : inside(s, x, y),
    );
  const canWalk = (x: number, y: number): boolean => {
    if (x < b.x0 + pad || x > b.x1 - pad || y < b.y0 + pad || y > b.y1 - pad) return false;
    for (const c of scene.channels) {
      if (x > c.x0 && x < c.x1 && y > c.y0 && y < c.y1 && !c.openings.some((o) => x > o.x0 && x < o.x1 && y > o.y0 && y < o.y1)) return false;
    }
    if (body !== null && (!onGround(x, y) || body.some((p) => !onGround(x + p.x, y + p.y)))) return false;
    for (const c of colliders) {
      if (x > c.x0 - pad && x < c.x1 + pad && y > c.y0 - pad && y < c.y1 + pad) return false;
    }
    for (const c of dynamic) {
      if (x > c.x0 - pad && x < c.x1 + pad && y > c.y0 - pad && y < c.y1 + pad) return false;
    }
    return true;
  };
  // Surfaces by 2 m bucket over their bounding boxes (heightAt runs for every vertex of a projected shadow).
  const bucketSize = 2;
  const buckets = new Map<number, Surface[]>();
  const keyOf = (bx: number, by: number): number => (bx + 4096) * 8192 + (by + 4096);
  for (const s of surfaces) {
    const [x0, y0, x1, y1] =
      s.circle !== undefined
        ? [s.circle.x - s.circle.r, s.circle.y - s.circle.r, s.circle.x + s.circle.r, s.circle.y + s.circle.r]
        : s.polygon !== undefined
          ? [Math.min(...s.polygon.map((p) => p[0])), Math.min(...s.polygon.map((p) => p[1])), Math.max(...s.polygon.map((p) => p[0])), Math.max(...s.polygon.map((p) => p[1]))]
          : [s.x0 ?? 0, s.y0 ?? 0, s.x1 ?? 0, s.y1 ?? 0];
    for (let bx = Math.floor(x0 / bucketSize); bx <= Math.floor(x1 / bucketSize); bx += 1) {
      for (let by = Math.floor(y0 / bucketSize); by <= Math.floor(y1 / bucketSize); by += 1) {
        const k = keyOf(bx, by);
        const list = buckets.get(k);
        if (list === undefined) buckets.set(k, [s]);
        else list.push(s);
      }
    }
  }
  const standing = (x: number, y: number): { readonly id: string | null; readonly h: number } => {
    let height = 0;
    let id: string | null = null;
    // Surfaces keep their record order in a bucket, so an exclusive one wins as it does over the whole list.
    for (const s of buckets.get(keyOf(Math.floor(x / bucketSize), Math.floor(y / bucketSize))) ?? []) {
      if (!inside(s, x, y)) continue;
      const h = surfaceHeight(s, x);
      if (s.exclusive === true) return { id: s.id, h };
      if (id === null || h > height) id = s.id;
      height = Math.max(height, h);
    }
    return { id, h: height };
  };
  const heightAt = (x: number, y: number): number => standing(x, y).h;
  const i0 = Math.ceil(b.x0 / cell);
  const j0 = Math.ceil(b.y0 / cell);
  const columns = Math.floor(b.x1 / cell) - i0 + 1;
  const rows = Math.floor(b.y1 / cell) - j0 + 1;
  const walkable = new Uint8Array(columns * rows);
  const lattice = (): void => {
    for (let j = 0; j < rows; j += 1) for (let i = 0; i < columns; i += 1) walkable[j * columns + i] = canWalk((i + i0) * cell, (j + j0) * cell) ? 1 : 0;
  };
  lattice();
  return {
    scene,
    cell,
    i0,
    j0,
    columns,
    rows,
    walkable,
    canWalk,
    heightAt,
    surfaceAt: (x, y) => standing(x, y).id,
    node: (i, j) => {
      const ii = i - i0;
      const jj = j - j0;
      return ii >= 0 && jj >= 0 && ii < columns && jj < rows && walkable[jj * columns + ii] === 1;
    },
    setDynamic(next) {
      dynamic = [...next];
      lattice();
    },
    dynamic: () => dynamic,
  };
};
